import Button from "@/common/components/Button"
import { ImageUploader } from "@/common/components/ImageUploader"
import MaskedTextField from "@/common/components/MaskedTextField"
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple"
import TextField from "@/common/components/TextField"
import { cnpjCpfMask, numericRegexMask, stateInscriptionMask } from "@/common/constants/masks.constant"
import { useVaduCnpjDetails } from "@/common/hooks/queries/useVaduCnpjDetails"
import { VaduCnpjDetailsResponse } from "@/common/types/vaduCnpjDetails.type"
import { checkIsImage } from "@/common/utils/fileValidator"
import { unFormatCnpj } from "@/common/utils/unFormatters"
import { RequestedReview } from "@/features/establishmentLeadsFunnel/components/RequestedReview"
import { ReviewButtons } from "@/features/establishmentLeadsFunnel/components/ReviewButtons"
import { PrivateCompanyLeadsFunnelUpdateValidationSchema } from "@/features/privateCompanyLeadsFunnel/schemas/update.schema"
import { GeneralInformationData } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { UseMutateFunction } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { Controller, FieldErrors, useFormContext, UseFormRegister } from "react-hook-form"

interface GeneralInformationSectionProps {
  general_information?: GeneralInformationData
  register: UseFormRegister<PrivateCompanyLeadsFunnelUpdateValidationSchema>
  errors: FieldErrors<PrivateCompanyLeadsFunnelUpdateValidationSchema>
  submittableOnBlur?: (e: React.FocusEvent<HTMLInputElement, Element>, onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void) => void
  submit?: () => void
  approveGeneralInformationLead?: UseMutateFunction<void, Error, { description?: string } | undefined, unknown>
  declineGeneralInformationLead?: UseMutateFunction<void, Error, { description?: string } | undefined, unknown>
  disabled?: boolean
}

export const GeneralInformationSection = ({
  general_information,
  register,
  errors,
  submittableOnBlur,
  submit,
  approveGeneralInformationLead,
  declineGeneralInformationLead,
  disabled,
}: GeneralInformationSectionProps) => {
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const { control, setValue, getValues, watch } = useFormContext<PrivateCompanyLeadsFunnelUpdateValidationSchema>()

  const watchNewCompanyLogo = watch('general_information.new_company_logo');

  const successGetVaduCnpjDetails = (data: VaduCnpjDetailsResponse) => {
    const { general_information } = data.data.company;

    const oldGeneralInformation = getValues('general_information');

    setValue('general_information', {
      ...oldGeneralInformation,
      cnpj: general_information.cnpj || '',
      fantasy_name: general_information.fantasy_name || '',
      official_name: general_information.official_name || '',
      email: general_information.email || '',
      primary_cnae: unFormatCnpj(general_information.primary_cnae || ''),
    });

    submit?.();
  };

  const { mutate: getVaduCnpjDetails, isPending: vaduCnpjDetailsIsPending } = useVaduCnpjDetails({
    onSuccess: successGetVaduCnpjDetails,
    onError: (error) => {
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null)
    },
  });

  const handleImportCnpjData = () => {
    getVaduCnpjDetails(unFormatCnpj(general_information?.cnpj || ''));
  };

  useEffect(() => {
    if (watchNewCompanyLogo) {
      submit?.();
    }
  }, [watchNewCompanyLogo]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between">
        <p className="text-[18px] font-bold">Dados Gerais</p>
        {(approveGeneralInformationLead && declineGeneralInformationLead) && (
          <ReviewButtons
            reviewState={general_information?.last_review?.state}
            onApprove={approveGeneralInformationLead}
            onDecline={declineGeneralInformationLead}
          />
        )}
      </div>
      {general_information?.last_review?.state === 'disapproved' && (
        <RequestedReview description={general_information?.last_review?.description} />
      )}
      <div className="flex gap-2">
        <div className="flex gap-2 w-full">
          <ImageUploader
            isLoading={false}
            label="Foto"
            name="profile-picture"
            accept="image/*"
            onChange={(e) => {
              const file = (e.target as HTMLInputElement & { files: FileList }).files?.[0]
              if (!file || !checkIsImage(file)) {
                return
              }
              setValue?.("general_information.new_company_logo", file);
            }}
            currentImageUrl={watchNewCompanyLogo ?
              URL.createObjectURL(watchNewCompanyLogo!) :
              general_information?.company_logo?.url ?
                general_information?.company_logo?.url :
                undefined
            }
            disabled={disabled}
          />
          <div className="flex flex-col w-full items-start gap-y-1">
            <MaskedTextField
              mask={cnpjCpfMask}
              className="w-full mt-[14px]"
              label="CNPJ/CPF"
              defaultValue={general_information?.cnpj}
              errorMessage={errors?.general_information?.cnpj?.message || undefined}
              {...register('general_information.cnpj')}
              onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('general_information.cnpj').onBlur)}
              disabled
              markAsRequired
            />
            <Button
              variant="link"
              onClick={handleImportCnpjData}
              disabled={
                disabled ||
                !(general_information?.cnpj && general_information?.cnpj?.length >= 14) ||
                vaduCnpjDetailsIsPending
              }
              isLoading={vaduCnpjDetailsIsPending}
            >
              Importar dados
            </Button>
          </div>
        </div>
        <TextField
          label="Tipo de empresa"
          className="w-full mt-[14px]"
          disabled
          value='Privada'
          markAsRequired
        />
      </div>
      <div className="flex gap-2">
        <TextField
          label="Nome Fantasia"
          className="w-1/2"
          maxLength={64}
          defaultValue={general_information?.fantasy_name}
          errorMessage={errors?.general_information?.fantasy_name?.message || undefined}
          {...register('general_information.fantasy_name')}
          onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('general_information.fantasy_name').onBlur)}
          disabled={disabled}
          markAsRequired
        />
        <TextField
          label="Nome Empresarial"
          className="w-1/2"
          maxLength={64}
          defaultValue={general_information?.official_name}
          errorMessage={errors?.general_information?.official_name?.message || undefined}
          {...register('general_information.official_name')}
          onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('general_information.official_name').onBlur)}
          disabled={disabled}
          markAsRequired
        />
      </div>
      <TextField
        label="Endereço eletrônico"
        maxLength={64}
        defaultValue={general_information?.email}
        errorMessage={errors?.general_information?.email?.message || undefined}
        {...register('general_information.email')}
        onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('general_information.email').onBlur)}
        disabled={disabled}
        markAsRequired
      />
      <div className="flex gap-2">
        <Controller
          control={control}
          name='general_information.state_inscription'
          render={({ field }) => (
            <MaskedTextField
              label="IE (Inscrição Estadual)"
              mask={stateInscriptionMask}
              defaultValue={general_information?.state_inscription}
              errorMessage={errors?.general_information?.state_inscription?.message || undefined}
              className="w-1/2"
              {...field}
              onBlur={(e) => submittableOnBlur && submittableOnBlur(e, field.onBlur)}
              disabled={disabled}
              markAsRequired
            />
          )}
        />

        <Controller
          control={control}
          name='general_information.primary_cnae'
          render={({ field }) => (
            <MaskedTextField
              label="CNAE"
              mask={numericRegexMask(7)}
              defaultValue={general_information?.primary_cnae}
              errorMessage={errors?.general_information?.primary_cnae?.message || undefined}
              className="w-1/2"
              {...field}
              onBlur={(e) => submittableOnBlur && submittableOnBlur(e, field.onBlur)}
              disabled={disabled}
              markAsRequired
            />
          )}
        />
      </div>
      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </div>
  )
}
